import { useEffect, useState, type FormEvent } from "react";
import { Link2, X } from "lucide-react";
import { Avatar, Button, EmptyState, Field, FormRow, IconButton, FormDialog, Picker, Row, DialogCancel } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useInviteBandSeat,
  useCrewLookup,
  useRevokeSeatInvite,
  type BandSeatView,
  type InviteIssued,
  type PendingInvite,
} from "../../app/hooks/useBandTeam.js";
import { useBandContext, useIsPro } from "../../app/bandContext.js";
import { InviteLinkShare } from "../invites/InviteLinkShare.js";
import { InviteLinkDialog } from "../invites/InviteLinkDialog.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { bandRoleOptionsFor, pickerRoleLabel, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { RoleLabel } from "./RoleLabel.js";
import styles from "./YourCrewCard.module.scss";

export function CrewError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return <EmptyState title="Crew unavailable" description="Your role on this band doesn't include team management." />;
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState title="Couldn't load the crew" description={message} action={<Button onClick={onRetry}>Retry</Button>} />
  );
}

// ---- Member row --------------------------------------------------------------------------------------

export function CrewSeatRow({
  seat,
  isSelf,
  onOpen,
}: {
  seat: BandSeatView;
  isSelf: boolean;
  /** Open this member's profile. The row carries NO editing controls of its own — see below. */
  onOpen?: () => void;
}) {
  // ART-45: Pro does not show the acting label. It is merely not SHOWN — `seat.actingRole` is still served
  // and still stored, so an upgrade loses nothing and a downgrade brings the pill back over data that was
  // there the whole time.
  //
  // Pro shows NOTHING in its place, and that is deliberate (PO, 2026-09-10). The party pill that first went
  // here was redundant: on Pro this list is GROUPED BY PARTY, so every row under "B Party" wore a chip
  // saying "B Party" — a column of identical pills, which is the thing this card's own stylesheet already
  // warns against one rule down. The party is said ONCE, by the group heading. The profile modal keeps its
  // party pill because there is no grouping there and it is the only place a party is SET.
  const isPro = useIsPro();
  const name = seat.displayName || seat.email || "Unnamed member";

  // NO role dropdown here (PO ruling 2026-08-20). It used to sit inline, which cost more than it looked:
  // a ~420px <select> plus a delete button left the NAME column 11px wide on a phone, so a band could not
  // tell who was in their crew. Crew position and acting position are now set inside the member's profile
  // modal, where there is room for them and where the TM/AV gate is expressed once.
  return (
    <Row
      avatar={<PersonAvatar personId={seat.personId} avatarMediaId={seat.avatarMediaId} initials={orgInitials(name)} shape="circle" />}
      name={
        <>
          {name}
          {/* No OWNER badge (PO, 2026-08-20): the sub-line says "Artist" + the mark for that seat. */}
          {isSelf && !seat.isOwner && <span className={styles.badge}>You</span>}
        </>
      }
      // The SEAT role is the sub-line — it is the durable fact about this person, and it reads as a
      // description of them rather than as a control. When the person is also COVERING something (an acting
      // role), the two are shown as one "Role / Acting Role" — they are wearing both hats, "listed under both
      // roles", not one instead of the other (ART-164, PO). No separate pill: that read as a second, parallel
      // listing of the same person. Acting is Standard-only, so it never renders on Pro.
      sub={
        <>
          <RoleLabel role={seat.role} owner={seat.isOwner} />
          {!isPro && seat.actingRole ? <> / {ACTING_LABELS[seat.actingRole] ?? seat.actingRole}</> : null}
        </>
      }
      onOpen={onOpen}
    />
  );
}

/** The acting vocabulary's display names. Its own map because these are NOT seat roles — `RoleLabel` would
 * happily title-case `production_manager`, but routing them through it would imply they share a namespace. */
const ACTING_LABELS: Record<string, string> = {
  tour_manager: "Tour Manager",
  production_manager: "Production Manager",
  merch_manager: "Merch Manager",
};

/** The bare label, for the crew row's right-hand pill — that column only ever holds acting roles, so the
 * position already says "acting" and the word would be noise in a 2xs mono pill. */
export function ActingRoleLabel({ role }: { role: string }) {
  return <>{ACTING_LABELS[role] ?? role}</>;
}

/** The spelled-out form, for the profile modal and its menu — there the label sits beside the SEAT role, so
 * "Acting" is the word doing the distinguishing and dropping it would make the two look interchangeable. */
export function ActingRoleFullLabel({ role }: { role: string }) {
  return <>{ACTING_LABELS[role] ? `Acting ${ACTING_LABELS[role]}` : role}</>;
}

/** The three acting labels, in menu order — spelled out, as the menu shows them. */
export const ACTING_ROLES = Object.entries(ACTING_LABELS).map(([role, label]) => ({
  role,
  label: `Acting ${label}`,
}));

// ---- Pending crew invites (read-only — no band-side revoke route this slice) --------------------------

/**
 * A row must never show a role key or a bare mailbox where a person's name goes. An unknown-account
 * invite carries the names on its payload (the account-creation seed); an existing-account invite has
 * none — fall back to the email's local part, then "Invited member".
 */
export function invitedName(invite: PendingInvite): string {
  const full = [invite.firstName, invite.lastName].filter(Boolean).join(" ").trim();
  if (full) return full;
  const local = invite.email?.split("@")[0]?.trim();
  return local || "Invited member";
}

export function PendingInviteRow({ invite }: { invite: PendingInvite }) {
  const name = invitedName(invite);
  const revoke = useRevokeSeatInvite();
  const { bandId } = useBandContext();
  const [linkOpen, setLinkOpen] = useState(false);
  // No confirm dialog: revoking is cheap to undo (re-invite from the same modal), the removal is optimistic
  // with rollback, and this row only renders for the band:manage population that issued it.
  return (
    <Row
      pending
      avatar={<Avatar initials={orgInitials(name)} shape="circle" />}
      name={
        <>
          {name}
          <span className={styles.badgePending}>Invite sent</span>
        </>
      }
      sub={
        <>
          <RoleLabel role={invite.role} /> · expires {fmtDate(invite.expiresOn)}
        </>
      }
      trailing={
        <>
          {/* ART-239: a fresh /join link (the stored token is a hash, so the old one can't be shown again). */}
          <IconButton icon={Link2} label={`Copy invite link for ${name}`} size="sm" onClick={() => setLinkOpen(true)} />
          <IconButton
            icon={X}
            label={`Revoke invite for ${name}`}
            tone="danger"
            size="sm"
            disabled={revoke.isPending}
            onClick={() => invite.inviteId && revoke.mutate(invite.inviteId)}
          />
          {linkOpen && invite.inviteId && (
            <InviteLinkDialog
              target={{ kind: "band_seat", bandId, inviteId: invite.inviteId }}
              recipient={invite.email}
              onClose={() => setLinkOpen(false)}
            />
          )}
        </>
      }
    />
  );
}

// ---- Invite crew modal -------------------------------------------------------------------------------

/** ISO → locale date (expiry display; date-only). */
function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Add Crew (PO redesign, 2026-08-20) — TWO fields: email + role. A qualified email (with a TLD) or a
 * 7+-digit phone triggers a DEBOUNCED account search; a hit shows who they are ("add them" — their
 * account carries its own name), the already-states become designed messages, and only a MISS reveals
 * the First/Last fields — the seed the invitee's account is created from. No title (retired). Phone
 * INVITES stay email-only for now (roadmap: KNOWN-ISSUES).
 */
export function InviteCrewModal({ onClose }: { onClose: () => void }) {
  const invite = useInviteBandSeat();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // ⚠️ THE OPTIONS ARE TITLES, FROM THE SERVER — `POST /bands/{id}/team/invites` validates against
  // core.seat_titles. They were the hard-coded BAND_ROLES array of ROLE keys until 2026-09-04, and the titles
  // split had made five of its eight values invalid, so inviting a plain "Band Member" — the commonest crew
  // invite there is — was a 422. The default is the catalog's first entry rather than a named key, because a
  // named default is exactly what went stale here.
  const [picked, setPicked] = useState<string | null>(null);
  const isPro = useIsPro();
  const seatRoles = useSeatRoles();
  // ART-150: a Standard band is not offered the 15 Pro crew titles. Filtering a fresh invite is unconditional
  // (there is no current seat to preserve here); the band seat gate is the server backstop.
  const titleOptions = bandRoleOptionsFor(seatRoles.data?.bandRoles, isPro);
  const role = picked ?? titleOptions[0]?.role ?? "";
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<InviteIssued | null>(null);

  // The debounced, QUALIFIED search key: a full email (local@domain.tld) or 7+ digits. Below the bar
  // the lookup never fires — half-typed addresses would just churn 400s.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const raw = email.trim();
    const qualified =
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw) || raw.replace(/\D/g, "").length >= 7 ? raw : "";
    const t = setTimeout(() => setQuery(qualified), 350);
    return () => clearTimeout(t);
  }, [email]);
  const lookup = useCrewLookup(query);

  const found = query.length > 0 && lookup.data?.found === true;
  const missing = query.length > 0 && lookup.data?.found === false;
  // ART-239: an artist account IS a band — it can be nobody's crew, so it blocks the send like the two
  // already-states do, with its own sentence (the server refuses it too, 422).
  const blocked = found && (lookup.data?.alreadyInBand || lookup.data?.alreadyInvited || lookup.data?.isArtistAccount);
  // An email invite is the only kind that can be SENT (phone invites are roadmap) — a phone-resolved
  // account still needs the email typed... unless the lookup already proves the account exists, in
  // which case the invite goes to the account's email server-side? No: the invite is keyed on the
  // typed email. Phone lookups INFORM ("they have an account"); the send button requires the email.
  const emailShaped = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const canSend = emailShaped && !blocked && (found || (firstName.trim() && lastName.trim()));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    invite.mutate(
      found
        ? { email: email.trim(), title: role }
        : { email: email.trim(), title: role, firstName: firstName.trim(), lastName: lastName.trim() },
      {
        onSuccess: (result) => {
          setIssued(result);
          setFirstName("");
          setLastName("");
          setEmail("");
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  // A FormDialog (ART-251): a bottom sheet on a phone, where this is reached from Contacts' "+ Add". The body is
  // the form only while composing — the issued state is a result, not a form, so its actions submit nothing.
  return (
    <FormDialog
      title="Add crew"
      onClose={onClose}
      bodyAs={issued ? "div" : "form"}
      onSubmit={issued ? undefined : onSubmit}
      footer={
        issued ? (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Done
            </Button>
            <Button type="button" onClick={() => setIssued(null)}>Invite another</Button>
          </>
        ) : (
          <>
            <DialogCancel />
            <Button type="submit" loading={invite.isPending} disabled={!canSend}>
              Send invite
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <div className={styles.inviteBody}>
          <p className={styles.issuedTitle}>Invite sent</p>
          {/* The server-built /join link (ART-239) — the wrapper's own origin is not the public one. */}
          {issued.joinUrl && (
            <InviteLinkShare url={issued.joinUrl} expiresOn={issued.expiresOn} recipient={issued.email} emailed />
          )}
        </div>
      ) : (
        <div className={styles.inviteBody}>
          <Field
            label="Email"
            type="email"
            placeholder="member@band.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hint={lookup.isLoading && query ? "Checking for an account…" : undefined}
          />
          <Picker
            id="crew-invite-role"
            label="Role"
            value={role}
            onChange={setPicked}
            disabled={titleOptions.length === 0}
            options={titleOptions.map((r) => ({
              value: r.role ?? "",     // SeatRoleOption.role carries the TITLE key
              // ART-171: a picker is the ONE place the full-access qualifier is spelled out. `pickerRoleLabel`
              // is the accessible name + search key ("Band Member (Full Access)"); `spellFullAccess` makes the
              // visible row and the mark's sr-only twin agree with it. Inert on every other title.
              label: pickerRoleLabel(r),
              render: <RoleLabel role={r.roleKey} titleKey={r.role} spellFullAccess />,
            }))}
          />

          {/* The search verdicts, as designed states rather than server 4xxs later. */}
          {found && lookup.data?.alreadyInBand && (
            <p className={styles.lookupNote} role="status">
              {lookup.data.displayName ?? "They"} — already on your crew.
            </p>
          )}
          {found && lookup.data?.isArtistAccount && !lookup.data?.alreadyInBand && (
            <p className={styles.lookupNote} role="status">
              {lookup.data.displayName ?? "That address"} is an artist account. Artist accounts can&rsquo;t join
              another band&rsquo;s crew — invite their personal address instead.
            </p>
          )}
          {found && !lookup.data?.alreadyInBand && !lookup.data?.isArtistAccount && lookup.data?.alreadyInvited && (
            <p className={styles.lookupNote} role="status">
              {lookup.data.displayName ?? "They"} already have a pending invite.
            </p>
          )}
          {found && !blocked && (
            <div className={styles.lookupHit} role="status">
              <PersonAvatar
                personId={lookup.data?.personId}
                avatarMediaId={lookup.data?.avatarMediaId}
                initials={orgInitials(lookup.data?.displayName ?? "?")}
                shape="circle"
              />
              <span className={styles.lookupName}>{lookup.data?.displayName}</span>
              <span className={styles.lookupMeta}>has an account — they&rsquo;ll get the invite in Artist CNTRL and by email</span>
            </div>
          )}

          {/* Only a MISS asks for names: they seed the account the invitee will create. */}
          {missing && (
            <>
              <p className={styles.lookupNote} role="status">
                No account yet! Add their name and we'll shoot them an invite.
              </p>
              <FormRow>
                <Field
                  label="First name"
                  placeholder="Casey"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Field
                  label="Last name"
                  placeholder="Bloom"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormRow>
            </>
          )}

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </FormDialog>
  );
}
